import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select"
import {Button} from "@/components/ui/button"
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card"
import type {WorkflowLogStatus} from "@/model/Log"
import {useNavigate, useParams} from "react-router-dom"
import WorkflowLogList from "@/view/workflowLog/workflowLogList"
import useJob from "@/query/workflowJob/useJob"
import useWorkflows from "@/query/workflow/useWorkflows"
import JobStatusBadge from "@/view/workflowJob/components/JobStatusBadge"
import JobProgressBar from "@/view/workflowJob/components/JobProgressBar"
import {formatIsoReadable} from "@/utils/dateUtils"
import {ChevronLeft, Download, Loader2} from "lucide-react"
import React, {useMemo, useState} from "react"
import workflowJobRepository from "@/repository/WorkflowJobRepository";

const ALL_STATUS = "all"

const WorkflowJobView = () => {
    const navigate = useNavigate()
    const {jobId} = useParams<{ jobId: string }>()
    const [statusFilter, setStatusFilter] = useState<WorkflowLogStatus | typeof ALL_STATUS>(ALL_STATUS)

    const {data: job, isLoading: isJobLoading} = useJob(jobId!)
    const {data: workflows} = useWorkflows()

    const workflowName = useMemo(() => {
        if (!job)
            return undefined

        return (workflows?.pages ?? [])
            .flatMap((page) => page.content)
            .find((workflow) => workflow._id === job.workflowId)
            ?.name
    }, [job, workflows])

    const logStatus = statusFilter === ALL_STATUS ? undefined : statusFilter

    const jobTitle = job
        ? (job.name ?? job.defaultParams?.chatbot_name ?? job._id)
        : undefined

    const handleStatusChange = (value: string) => {
        setStatusFilter(value === ALL_STATUS ? ALL_STATUS : value as WorkflowLogStatus)
    }

    const onClickDownload = async () => {
        if (!jobId) {
            return
        }

        try {
            const url = await workflowJobRepository.export(jobId);
            const link = document.createElement("a");
            link.href = url;
            link.click();
        } catch (e) {
            console.error(e)
        }
    }

    return (
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden p-2">
            <Card className="shrink-0">
                <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                        <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => navigate("/workflow-service/jobs")}
                        >
                            <ChevronLeft className="h-4 w-4"/>
                        </Button>
                        <div className="flex min-w-0 flex-1 gap-2">
                            {(job || (isJobLoading && !job)) && (
                                <div className="flex flex-wrap items-center gap-2">
                                    {job && <JobStatusBadge job={job}/>}
                                    {isJobLoading && !job && (
                                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground"/>
                                    )}
                                </div>
                            )}
                            <CardTitle className="text-lg leading-snug">
                                {jobTitle ?? (isJobLoading ? "Loading..." : "Job Results")}
                            </CardTitle>
                        </div>
                    </div>
                </CardHeader>

                {job && (
                    <CardContent className="flex flex-col gap-4 pt-0">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                            <JobMetaItem label="Type" value={job.type}/>
                            {workflowName && (
                                <JobMetaItem label="Workflow" value={workflowName}/>
                            )}
                            <JobMetaItem label="Created At" value={formatIsoReadable(job.createdAt)}/>
                        </div>

                        {job.status === "running" && (
                            <JobProgressBar progressRate={job.progressRate}/>
                        )}
                    </CardContent>
                )}
            </Card>

            <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <CardHeader
                    className="flex shrink-0 flex-col gap-4 border-b py-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-col gap-1">
                        <CardTitle className="text-base">Query Results</CardTitle>
                        <CardDescription>Filter logs by execution status</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                        <Select value={statusFilter} onValueChange={handleStatusChange}>
                            <SelectTrigger className="w-[160px] bg-background">
                                <SelectValue placeholder="Status"/>
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ALL_STATUS}>All</SelectItem>
                                <SelectItem value="done">Done</SelectItem>
                                <SelectItem value="failed">Failed</SelectItem>
                            </SelectContent>
                        </Select>
                        <Button type="button"
                                variant="outline"
                                size="sm"
                                disabled={job?.status === "running"}
                                onClick={() => onClickDownload()}
                        >
                            <Download className="h-4 w-4"/>
                            Download
                        </Button>
                    </div>
                </CardHeader>
                <CardContent className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
                    <WorkflowLogList groupId={jobId} status={logStatus}/>
                </CardContent>
            </Card>
        </div>
    )
}


interface JobMetaItemProps {
    label: string
    value: string
}

const JobMetaItem = ({label, value}: JobMetaItemProps) => (
    <div className="flex flex-col gap-1 rounded-lg border bg-muted/40 px-3 py-2.5">
        <div className="text-xs font-medium text-muted-foreground">{label}</div>
        <div className="truncate text-sm font-medium">{value}</div>
    </div>
)

export default WorkflowJobView
